import React from 'react';
import { useTranslation } from 'react-i18next';
// import { FcGoogle } from 'react-icons/fc';
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router';

import { Assets } from '~/assets.g/assets';
import { AppRoutes } from '~/core/router/routes';
import { isValidEmail } from '~/core/utils/email_validator';
import { CustomButton } from '~/shared/components/custom_button';
import { CustomInput } from '~/shared/components/custom_input_field';

import { LoginHandler } from '../../application/login_handler';
import { AuthLayout } from '../layouts/auth_layout';

export const loader = LoginHandler.loginLoader;
export const action = LoginHandler.loginAction;

export default function LoginPage() {
  const { t } = useTranslation(['auth']);
  const navigate = useNavigate();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();

  const currentStep = Number.parseInt(searchParams.get('step') || '1', 10);
  // const [currentStep, setCurrentStep] = React.useState<number>(1);

  const [formErrors, setFormErrors] = React.useState<{
    email?: string;
  }>({});
  const [resetEmail, setResetEmail] = React.useState('');
  const [isLocalSubmitting, setIsLocalSubmitting] = React.useState(false);

  const { authFormData, version } = useLoaderData<typeof LoginHandler.loginLoader>();
  const actionData = useActionData<typeof LoginHandler.loginAction>();

  // Get organization and email from URL params
  const selectedOrg = searchParams.get('selectedOrganization');
  const userEmail = searchParams.get('userEmail');

  const isSubmitting = navigation.state === 'submitting';

  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [prevStep, setPrevStep] = React.useState(currentStep);

  React.useEffect(() => {
    if (prevStep !== currentStep) {
      setIsStepLoading(true);
      setPrevStep(currentStep);

      // Simulate network/transition delay
      const timer = setTimeout(() => {
        setIsStepLoading(false);
      }, 500); // Adjust duration if needed

      return () => clearTimeout(timer);
    }
  }, [currentStep, prevStep]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;

    const errors: typeof formErrors = {};

    if (!isValidEmail(email)) {
      errors.email = t('invalidEmail');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLocalSubmitting(true);
    event.currentTarget.submit();
  };

  const renderStep1 = () => (
    <Form
      method="post"
      className="flex flex-col"
    >
      <CustomInput
        name="organizationName"
        label={t('organizationName')}
        labelType="required"
        placeholder={t('enterOrganizationName')}
        defaultValue={
          selectedOrg
            ? JSON.parse(selectedOrg).name
            : authFormData?.inviteInfo?.organization?.name || ''
        }
      />

      <div className="mb-6 md:mb-8" />

      <CustomInput
        name="email"
        type="email"
        label={t('workEmail')}
        labelType="required"
        placeholder={t('enterWorkMail')}
        // readOnly={!!authFormData?.inviteInfo}
        defaultValue={userEmail || authFormData?.inviteInfo?.user?.email || ''}
      />

      <div className="mb-6 md:mb-8" />

      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        isLoading={isSubmitting || isLocalSubmitting}
        type="submit"
      >
        {t('continue')}
      </CustomButton>
    </Form>
  );

  const renderStep2 = () => (
    <Form
      method="post"
      className="flex flex-col"
      onSubmit={handleSubmit}
    >
      <div className="mb-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <p className="text-sm md:text-base">
            <span className="text-neutralDark">{t('loggingInAs')}</span>
          </p>
          <p className="break-words text-sm font-semibold md:flex-1 md:text-base">
            {authFormData?.email || authFormData?.inviteInfo?.user?.email}
          </p>

          {authFormData?.organizationName && (
            <CustomButton
              variant="primary"
              buttonType="text"
              onClick={() => navigate('/login')}
              type="button"
              className="self-start md:ml-auto md:flex-shrink-0"
            >
              {t('change')}
            </CustomButton>
          )}
        </div>
      </div>
      <CustomInput
        name="organizationName"
        label={t('organizationName')}
        placeholder={t('enterOrganizationName')}
        value={authFormData?.organizationName || ''}
        readOnly
      />

      <div className="mb-6 md:mb-8" />
      <CustomInput
        name="email"
        type="email"
        label={t('yourEmail')}
        placeholder={t('enterWorkMail')}
        value={authFormData?.email || ''}
        readOnly
      />

      <div className="mb-6 md:mb-8" />

      <CustomInput
        name="password"
        label={t('enterPassword')}
        labelType="required"
        placeholder={t('password')}
        type="password"
      />

      <div className="mb-4 md:mb-5" />

      <div>
        <CustomButton
          variant="primary"
          buttonType="text"
          onClick={() => navigate(`${AppRoutes.LOGIN.path}?step=3`)}
          type="button"
        >
          {t('forgotPassword')}
        </CustomButton>
      </div>

      <div className="mb-5 md:mb-6" />

      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        isLoading={isSubmitting || isLocalSubmitting}
        type="submit"
      >
        {t('continue')}
      </CustomButton>
    </Form>
  );

  const renderStep3 = () => (
    <Form
      method="post"
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        setIsLocalSubmitting(true);
        const formData = new FormData(e.currentTarget);
        const email = formData.get('email') as string;
        setResetEmail(email); // Save email for step 4
        setTimeout(() => {
          setIsLocalSubmitting(false);
          navigate(`${AppRoutes.LOGIN.path}?step=4`);
        }, 500);
      }}
    >
      <CustomInput
        name="email"
        label={t('yourEmail')}
        labelType="required"
        placeholder={t('enterWorkMail')}
        defaultValue={authFormData?.email || ''}
      />

      <div className="mb-5 md:mb-6" />

      <CustomButton
        variant="primary"
        buttonType="filled"
        isFullWidth
        type="submit"
        isLoading={isSubmitting || isLocalSubmitting}
      >
        {t('resetPassword')}
      </CustomButton>
    </Form>
  );

  const renderStep4 = () => (
    <Form
      method="post"
      className="flex flex-col"
    >
      <div className="mb-4 md:mb-5" />
      <div className="flex flex-col gap-3 md:flex-row md:gap-5">
        <CustomButton
          variant="surface"
          buttonType="outlined"
          isFullWidth
          type="submit"
          isLoading={isSubmitting || isLocalSubmitting}
        >
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="flex-shrink-0">
              <img
                src={Assets?.images?.google}
                alt="Gmail"
                width={24}
                height={24}
                className="h-6 w-6 bg-transparent object-cover md:h-8 md:w-8"
              />
            </span>
            <span className="text-sm md:text-base">{t('openGmail')}</span>
          </div>
        </CustomButton>
        <CustomButton
          variant="surface"
          buttonType="outlined"
          isFullWidth
          type="submit"
          isLoading={isSubmitting || isLocalSubmitting}
        >
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="flex-shrink-0">
              <img
                src={Assets?.images?.outlook}
                alt="Outlook"
                width={24}
                height={24}
                className="h-6 w-6 bg-transparent object-cover md:h-8 md:w-8"
              />
            </span>
            <span className="text-sm md:text-base">{t('openOutlook')}</span>
          </div>
        </CustomButton>
      </div>
    </Form>
  );

  return (
    <AuthLayout
      version={version}
      error={actionData?.error}
    >
      <div className="flex h-full w-full flex-col">
        <h4 className="h4 text-xl font-semibold leading-tight text-primarySemiDark md:text-2xl lg:text-3xl">
          {currentStep === 3
            ? t('resetPasswordHeading')
            : currentStep === 4
              ? t('checkYourEmail')
              : t('signInToAccount')}
        </h4>

        <div className="mb-2 md:mb-3" />

        <p className="text-sm leading-relaxed text-neutralDark md:text-base">
          {currentStep === 3
            ? t('forgotEmailSuggestion')
            : currentStep === 4
              ? t('resetEmailSentMessage', { email: resetEmail })
              : t('emailSuggestionLogin')}
        </p>

        <div className="mb-6 md:mb-8" />

        {/* <div className="flex w-full flex-col">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div> */}

        <div className="flex w-full flex-col">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* <div className="mt-8 flex items-center justify-center gap-2">
          <p className="text-sm text-neutralMedium">{t('newToRaithu')}</p>
          <CustomButton
            variant="primary"
            buttonType="text"
            onClick={() => navigate(AppRoutes.SIGNUP.path)}
          >
            {t('createAnAccount')}
          </CustomButton>
        </div> */}
      </div>
    </AuthLayout>
  );
}
